import React, { useEffect, useState } from 'react';
import { collection, query, orderBy, getDocs } from 'firebase/firestore';
import { db } from '../utils/firebase';
import { Event } from '../types';
import { Loader, Calendar, MapPin, Clock } from 'lucide-react';
import { Link } from 'react-router-dom';

const EventList: React.FC = () => {
  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchEvents = async () => {
      setLoading(true);
      try {
        const q = query(collection(db, 'events'), orderBy('date', 'asc'));
        const querySnapshot = await getDocs(q);
        const list: Event[] = [];
        querySnapshot.forEach((doc) => {
          list.push({ id: doc.id, ...doc.data() } as Event);
        });
        setEvents(list);
      } catch (error) {
        console.error("Error fetching events:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchEvents();
  }, []);

  if (loading) return (
    <div className="min-h-[50vh] flex items-center justify-center">
        <Loader className="animate-spin" />
    </div>
  );

  return (
    <div className="pt-32 pb-20 px-4 md:px-8 max-w-[1400px] mx-auto min-h-screen">
      <div className="border-b border-black pb-4 mb-12 flex justify-between items-end">
        <h1 className="font-heading text-4xl md:text-6xl font-bold tracking-tighter uppercase">Community Events</h1>
        <p className="hidden md:block text-sm text-gray-500 font-bold uppercase tracking-widest mb-2">Connect with the local scene</p>
      </div>

      {events.length === 0 ? (
        <div className="text-center py-20 text-gray-500">
          <p>No upcoming events scheduled. Check back soon!</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
          {events.map((event) => (
            <Link to={`/event/${event.id}`} key={event.id} className="group block bg-white border border-gray-100 hover:border-black transition-colors md:col-span-6 lg:col-span-4">
              <div className="relative aspect-video overflow-hidden bg-gray-100">
                <img
                  src={event.imageUrl || '/assets/placeholder.svg'}
                  alt={event.title}
                  onError={(e) => {
                    const t = e.currentTarget;
                    if (!t.dataset.fallback) { t.dataset.fallback = '1'; t.src = '/assets/placeholder.svg'; }
                  }}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                 <div className="absolute top-4 left-4">
                  <span className={`px-2 py-1 text-[10px] font-bold tracking-widest uppercase text-white ${event.status === 'upcoming' ? 'bg-black' : 'bg-gray-500'}`}>
                    {event.status}
                  </span>
                </div>
              </div>
              
              <div className="p-6">
                <div className="flex items-center gap-2 text-xs font-bold text-gray-400 mb-3 uppercase tracking-widest">
                   <Calendar size={14} className="text-black" />
                   <span>{new Date(event.date).toLocaleDateString(undefined, { weekday: 'short', month: 'long', day: 'numeric' })}</span>
                </div>
                
                <h3 className="font-heading font-black text-2xl uppercase leading-none mb-2 group-hover:text-brand-gold transition-colors">
                  {event.title}
                </h3>
                
                {event.subtitle && (
                   <p className="font-heading font-bold text-xs uppercase tracking-widest text-gray-500 mb-4">{event.subtitle}</p>
                )}

                <div className="flex flex-col gap-2 pt-4 border-t border-gray-100 mt-2">
                   {event.location && (
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                         <MapPin size={16} />
                         <span>{event.location}</span>
                      </div>
                   )}
                   <div className="flex items-center gap-2 text-sm text-gray-600">
                      <Clock size={16} />
                      <span>{new Date(event.date).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}</span>
                   </div>
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

export default EventList;
